import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useHotel } from '@/context/HotelContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, addDays } from 'date-fns';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/errorUtils';

import { API } from '@/config/api';

const STATUS_COLORS = {
  pending: { bg: 'bg-amber-400', text: 'text-amber-900', label: 'Pending' },
  confirmed: { bg: 'bg-blue-400', text: 'text-blue-900', label: 'Confirmed' },
  checked_in: { bg: 'bg-emerald-500', text: 'text-white', label: 'Checked In' },
  checked_out: { bg: 'bg-slate-300', text: 'text-slate-700', label: 'Checked Out' },
  cancelled: { bg: 'bg-red-300', text: 'text-red-900', label: 'Cancelled' }
};

const CalendarPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { rooms, roomTypes } = useHotel();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [reservations, setReservations] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Quick booking dialog state
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    guest_id: '',
    new_guest_first_name: '',
    new_guest_last_name: '',
    check_out_date: '',
    adults: 1,
    children: 0,
    rate_per_night: 100,
    status: 'confirmed',
    is_new_guest: false
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Guest info popup state
  const [showGuestInfo, setShowGuestInfo] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);

  // Get days for the current month view
  const daysInMonth = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  // Fetch reservations and guests
  const fetchData = useCallback(async (showIndicator = true) => {
    if (!user?.hotel_id) return;
    if (showIndicator) setLoading(true);
    try {
      const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      
      const [resResponse, guestsResponse] = await Promise.all([
        axios.get(`${API}/hotels/${user.hotel_id}/reservations?from_date=${start}&to_date=${end}`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API}/hotels/${user.hotel_id}/guests`, {
          headers: getAuthHeaders()
        })
      ]);
      
      setReservations(resResponse.data);
      setGuests(guestsResponse.data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    }
    setLoading(false);
  }, [user?.hotel_id, currentMonth, getAuthHeaders]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const intervalId = setInterval(() => fetchData(false), 15000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  const getGuestName = (guestId) => {
    const guest = guests.find(g => g.id === guestId);
    return guest ? `${guest.first_name?.substring(0, 3)}...` : '';
  };

  const getGuestFullName = (guestId) => {
    const guest = guests.find(g => g.id === guestId);
    return guest ? `${guest.first_name} ${guest.last_name}` : 'Unknown';
  };

  const getRoomTypeName = (roomTypeId) => {
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    return roomType?.name || '';
  };

  const getRoomPrice = (roomTypeId) => {
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    return roomType?.base_rate || roomType?.base_price || 100;
  };

  // Sort rooms by room number
  const sortedRooms = useMemo(() => {
    return [...rooms].sort((a, b) => {
      const numA = parseInt(a.number || a.room_number) || 0;
      const numB = parseInt(b.number || b.room_number) || 0;
      return numA - numB;
    });
  }, [rooms]);

  // Get reservation for a specific room and day
  const getReservationForCell = (roomId, day) => {
    return reservations.find(res => {
      if (res.room_id !== roomId) return false;
      const checkIn = new Date(res.check_in_date);
      const checkOut = new Date(res.check_out_date);
      checkIn.setHours(0, 0, 0, 0);
      checkOut.setHours(0, 0, 0, 0);
      const dayDate = new Date(day);
      dayDate.setHours(0, 0, 0, 0);
      return dayDate >= checkIn && dayDate < checkOut;
    });
  };

  // Check if this is the start day of a reservation
  const isReservationStart = (reservation, day) => {
    const checkIn = new Date(reservation.check_in_date);
    checkIn.setHours(0, 0, 0, 0);
    const dayDate = new Date(day);
    dayDate.setHours(0, 0, 0, 0);
    return checkIn.getTime() === dayDate.getTime();
  };

  // Calculate reservation span within current month view
  const getReservationSpan = (reservation, startDay) => {
    const checkOut = new Date(reservation.check_out_date);
    checkOut.setHours(0, 0, 0, 0);
    const monthEnd = endOfMonth(currentMonth);
    const effectiveEnd = checkOut > monthEnd ? monthEnd : checkOut;
    const startDate = new Date(startDay);
    startDate.setHours(0, 0, 0, 0);
    
    let span = 0;
    let current = new Date(startDate);
    while (current < effectiveEnd && current <= monthEnd) {
      span++;
      current.setDate(current.getDate() + 1);
    }
    return span;
  };

  // Handle cell click for quick booking
  const handleCellClick = (room, day) => {
    const reservation = getReservationForCell(room.id, day);
    if (reservation) {
      // If there's already a reservation, could show details or edit
      toast.info(`Room ${room.room_number} is booked by ${getGuestFullName(reservation.guest_id)}`);
      return;
    }
    
    // Open quick booking dialog
    setSelectedRoom(room);
    setSelectedDate(day);
    const defaultCheckout = format(addDays(day, 1), 'yyyy-MM-dd');
    setBookingForm({
      guest_id: '',
      new_guest_first_name: '',
      new_guest_last_name: '',
      check_out_date: defaultCheckout,
      adults: 1,
      children: 0,
      rate_per_night: getRoomPrice(room.room_type_id),
      status: 'confirmed',
      is_new_guest: false
    });
    setShowBookingDialog(true);
  };

  // Handle reservation click to show guest info
  const handleReservationClick = (reservation) => {
    setSelectedReservation(reservation);
    setShowGuestInfo(true);
  };

  // Get full guest object
  const getGuestDetails = (guestId) => {
    return guests.find(g => g.id === guestId) || null;
  };

  // Get room details
  const getRoomDetails = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return null;
    const roomType = roomTypes.find(rt => rt.id === room.room_type_id);
    return { ...room, roomTypeName: roomType?.name || 'Unknown' };
  };

  // Handle quick booking submission
  const handleQuickBooking = async (e) => {
    e.preventDefault();
    if (!selectedRoom || !selectedDate) return;
    
    setSubmitting(true);
    try {
      let guestId = bookingForm.guest_id;
      
      // Create new guest if needed
      if (bookingForm.is_new_guest) {
        if (!bookingForm.new_guest_first_name || !bookingForm.new_guest_last_name) {
          toast.error('Please enter guest name');
          setSubmitting(false);
          return;
        }
        
        const guestResponse = await axios.post(
          `${API}/hotels/${user.hotel_id}/guests`,
          {
            first_name: bookingForm.new_guest_first_name,
            last_name: bookingForm.new_guest_last_name
          },
          { headers: getAuthHeaders() }
        );
        guestId = guestResponse.data.id;
        setGuests(prev => [guestResponse.data, ...prev]);
      }
      
      if (!guestId) {
        toast.error('Please select or create a guest');
        setSubmitting(false);
        return;
      }
      
      // Create reservation
      const reservationData = {
        guest_id: guestId,
        room_id: selectedRoom.id,
        check_in_date: `${format(selectedDate, 'yyyy-MM-dd')}T14:00:00`,
        check_out_date: `${bookingForm.check_out_date}T11:00:00`,
        adults: bookingForm.adults,
        children: bookingForm.children,
        rate_per_night: bookingForm.rate_per_night,
        status: bookingForm.status
      };
      
      await axios.post(
        `${API}/hotels/${user.hotel_id}/reservations`,
        reservationData,
        { headers: getAuthHeaders() }
      );
      
      toast.success('Reservation created successfully!');
      setShowBookingDialog(false);
      fetchData(); // Refresh calendar
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create reservation'));
    }
    setSubmitting(false);
  };

  const today = new Date();

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Calendar</h1>
          <p className="text-sm text-muted-foreground">Room availability - Tap empty cell to book</p>
        </div>
        <Button data-testid="new-booking-btn" size="sm" onClick={() => window.location.href = '/reservations'}>
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">New Booking</span>
        </Button>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <Button 
          variant="outline"
          size="sm"
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          data-testid="prev-month-btn"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline ml-1">Previous</span>
        </Button>
        <h2 className="text-base sm:text-xl font-semibold" data-testid="current-month">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <Button 
          variant="outline"
          size="sm"
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          data-testid="next-month-btn"
        >
          <span className="hidden sm:inline mr-1">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 sm:gap-6 flex-wrap text-xs sm:text-sm">
        {Object.entries(STATUS_COLORS).map(([status, colors]) => (
          <div key={status} className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded ${colors.bg}`}></div>
            <span className="text-sm text-muted-foreground">{colors.label}</span>
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="border rounded-lg overflow-hidden bg-card">
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
          <table className="w-full min-w-[800px] sm:min-w-[1200px]">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-muted-foreground sticky left-0 bg-muted/30 z-10 min-w-[80px] sm:min-w-[120px] text-xs sm:text-sm">
                  Room
                </th>
                {daysInMonth.map((day) => {
                  const isToday = isSameDay(day, today);
                  return (
                    <th 
                      key={day.toISOString()} 
                      className={`text-center py-1 sm:py-2 px-0.5 sm:px-1 min-w-[32px] sm:min-w-[45px] ${isToday ? 'bg-primary/10' : ''}`}
                    >
                      <div className={`text-xs sm:text-sm font-medium ${isToday ? 'text-primary' : ''}`}>
                        {format(day, 'd')}
                      </div>
                      <div className={`text-[10px] sm:text-xs ${isToday ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                        {format(day, 'EEE')}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={daysInMonth.length + 1} className="py-8 text-center text-muted-foreground">
                    Loading calendar...
                  </td>
                </tr>
              ) : sortedRooms.length === 0 ? (
                <tr>
                  <td colSpan={daysInMonth.length + 1} className="py-8 text-center text-muted-foreground">
                    No rooms configured. Add rooms in the Rooms page.
                  </td>
                </tr>
              ) : (
                sortedRooms.map((room) => {
                  const processedReservations = new Set();
                  
                  return (
                    <tr key={room.id} className="border-b hover:bg-muted/20">
                      <td className="py-2 sm:py-4 px-2 sm:px-4 sticky left-0 bg-card z-10 border-r">
                        <div className="font-semibold text-xs sm:text-base">{room.number || room.room_number}</div>
                        <div className="text-[10px] sm:text-xs text-muted-foreground hidden sm:block">{getRoomTypeName(room.room_type_id)}</div>
                      </td>
                      {daysInMonth.map((day) => {
                        const isToday = isSameDay(day, today);
                        const reservation = getReservationForCell(room.id, day);
                        
                        // Skip if this reservation has already been rendered
                        if (reservation && processedReservations.has(reservation.id)) {
                          return null;
                        }
                        
                        // If no reservation, render clickable empty cell
                        if (!reservation) {
                          return (
                            <td 
                              key={day.toISOString()} 
                              className={`py-4 px-1 cursor-pointer hover:bg-primary/10 transition-colors ${isToday ? 'bg-primary/5' : ''}`}
                              onClick={() => handleCellClick(room, day)}
                              data-testid={`cell-${room.number || room.room_number}-${format(day, 'yyyy-MM-dd')}`}
                            >
                            </td>
                          );
                        }
                        
                        // Calculate span and render reservation bar
                        const isStart = isReservationStart(reservation, day);
                        if (isStart || !processedReservations.has(reservation.id)) {
                          processedReservations.add(reservation.id);
                          const span = getReservationSpan(reservation, day);
                          const status = reservation.status || 'pending';
                          const colors = STATUS_COLORS[status] || STATUS_COLORS.pending;
                          
                          return (
                            <td 
                              key={day.toISOString()} 
                              colSpan={span}
                              className={`py-4 px-0.5 ${isToday ? 'bg-primary/5' : ''}`}
                            >
                              <div 
                                className={`${colors.bg} ${colors.text} rounded px-2 py-1 text-xs font-medium truncate cursor-pointer hover:opacity-90 transition-opacity`}
                                title={`${getGuestFullName(reservation.guest_id)} - ${reservation.status}`}
                                onClick={() => handleReservationClick(reservation)}
                                data-testid={`reservation-bar-${reservation.id}`}
                              >
                                {getGuestName(reservation.guest_id)}
                              </div>
                            </td>
                          );
                        }
                        
                        return null;
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Quick Booking
              <Button variant="ghost" size="icon" onClick={() => setShowBookingDialog(false)}>
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          {selectedRoom && selectedDate && (
            <form onSubmit={handleQuickBooking} className="space-y-4">
              {/* Room & Date Info */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Room:</span>
                    <span className="ml-2 font-medium">{selectedRoom.room_number} ({getRoomTypeName(selectedRoom.room_type_id)})</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Check-in:</span>
                    <span className="ml-2 font-medium">{format(selectedDate, 'MMM d, yyyy')}</span>
                  </div>
                </div>
              </div>

              {/* Guest Selection */}
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <Button 
                    type="button" 
                    variant={!bookingForm.is_new_guest ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBookingForm({ ...bookingForm, is_new_guest: false })}
                  >
                    Existing Guest
                  </Button>
                  <Button 
                    type="button" 
                    variant={bookingForm.is_new_guest ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBookingForm({ ...bookingForm, is_new_guest: true, guest_id: '' })}
                  >
                    New Guest
                  </Button>
                </div>

                {!bookingForm.is_new_guest ? (
                  <div className="space-y-2">
                    <Label>Select Guest</Label>
                    <Select 
                      value={bookingForm.guest_id} 
                      onValueChange={(value) => setBookingForm({ ...bookingForm, guest_id: value })}
                    >
                      <SelectTrigger data-testid="guest-select">
                        <SelectValue placeholder="Select a guest" />
                      </SelectTrigger>
                      <SelectContent>
                        {guests.map((guest) => (
                          <SelectItem key={guest.id} value={guest.id}>
                            {guest.first_name} {guest.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>First Name *</Label>
                      <Input
                        value={bookingForm.new_guest_first_name}
                        onChange={(e) => setBookingForm({ ...bookingForm, new_guest_first_name: e.target.value })}
                        placeholder="John"
                        data-testid="new-guest-firstname"
                        required={bookingForm.is_new_guest}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name *</Label>
                      <Input
                        value={bookingForm.new_guest_last_name}
                        onChange={(e) => setBookingForm({ ...bookingForm, new_guest_last_name: e.target.value })}
                        placeholder="Doe"
                        data-testid="new-guest-lastname"
                        required={bookingForm.is_new_guest}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Check-out Date */}
              <div className="space-y-2">
                <Label>Check-out Date</Label>
                <Input
                  type="date"
                  value={bookingForm.check_out_date}
                  onChange={(e) => setBookingForm({ ...bookingForm, check_out_date: e.target.value })}
                  min={format(addDays(selectedDate, 1), 'yyyy-MM-dd')}
                  data-testid="checkout-date-input"
                  required
                />
              </div>

              {/* Guests & Rate */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Adults</Label>
                  <Input
                    type="number"
                    min="1"
                    value={bookingForm.adults}
                    onChange={(e) => setBookingForm({ ...bookingForm, adults: parseInt(e.target.value) || 1 })}
                    data-testid="adults-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Children</Label>
                  <Input
                    type="number"
                    min="0"
                    value={bookingForm.children}
                    onChange={(e) => setBookingForm({ ...bookingForm, children: parseInt(e.target.value) || 0 })}
                    data-testid="children-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rate/Night</Label>
                  <Input
                    type="number"
                    min="0"
                    value={bookingForm.rate_per_night}
                    onChange={(e) => setBookingForm({ ...bookingForm, rate_per_night: parseFloat(e.target.value) || 0 })}
                    data-testid="rate-input"
                  />
                </div>
              </div>

              {/* Total Rate Summary */}
              {(() => {
                const nights = bookingForm.check_out_date && selectedDate
                  ? Math.max(1, Math.round((new Date(bookingForm.check_out_date) - selectedDate) / 86400000))
                  : 0;
                const rate = parseFloat(bookingForm.rate_per_night) || 0;
                const total = rate * nights;
                return rate > 0 && nights > 0 ? (
                  <div className="rounded-lg border bg-muted/40 p-3 space-y-1.5" data-testid="quick-booking-total-summary">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>€{rate.toFixed(2)} × {nights} night{nights !== 1 ? 's' : ''}</span>
                      <span>€{total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base border-t pt-1.5">
                      <span>Total Rate (€)</span>
                      <span data-testid="quick-booking-total-amount">€{total.toFixed(2)}</span>
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Status */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={bookingForm.status} 
                  onValueChange={(value) => setBookingForm({ ...bookingForm, status: value })}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowBookingDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="create-booking-btn">
                  {submitting ? 'Creating...' : 'Create Booking'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Guest Info Dialog */}
      <Dialog open={showGuestInfo} onOpenChange={setShowGuestInfo}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <svg className="h-5 w-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              Reservation Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedReservation && (() => {
            const guest = getGuestDetails(selectedReservation.guest_id);
            const room = getRoomDetails(selectedReservation.room_id);
            const status = selectedReservation.status || 'pending';
            const colors = STATUS_COLORS[status] || STATUS_COLORS.pending;
            
            return (
              <div className="space-y-4">
                {/* Status Badge */}
                <div className="flex justify-center">
                  <span className={`${colors.bg} ${colors.text} px-4 py-1.5 rounded-full text-sm font-semibold`}>
                    {colors.label}
                  </span>
                </div>
                
                {/* Guest Information */}
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Guest Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name</span>
                      <span className="font-medium">{guest ? `${guest.first_name} ${guest.last_name}` : 'Unknown'}</span>
                    </div>
                    {guest?.email && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Email</span>
                        <span className="font-medium">{guest.email}</span>
                      </div>
                    )}
                    {guest?.phone && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Phone</span>
                        <span className="font-medium">{guest.phone}</span>
                      </div>
                    )}
                    {guest?.nationality && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Nationality</span>
                        <span className="font-medium">{guest.nationality}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Room & Dates */}
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Reservation Details</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Room</span>
                      <span className="font-medium">
                        {room ? `${room.number || room.room_number} - ${room.roomTypeName}` : 'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Check-in</span>
                      <span className="font-medium">{selectedReservation.check_in_date?.slice(0, 10)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Check-out</span>
                      <span className="font-medium">{selectedReservation.check_out_date?.slice(0, 10)}</span>
                    </div>
                    {(selectedReservation.adults || selectedReservation.num_guests) && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Guests</span>
                        <span className="font-medium">
                          {selectedReservation.adults || selectedReservation.num_guests} Adult(s)
                          {selectedReservation.children > 0 && `, ${selectedReservation.children} Child(ren)`}
                        </span>
                      </div>
                    )}
                    {selectedReservation.total_amount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Amount</span>
                        <span className="font-medium">€{selectedReservation.total_amount?.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedReservation.source && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Source</span>
                        <span className="font-medium capitalize">{selectedReservation.source.replace('_', ' ')}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowGuestInfo(false)}
                  >
                    Close
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={() => {
                      setShowGuestInfo(false);
                      window.location.href = `/reservations?id=${selectedReservation.id}`;
                    }}
                  >
                    View Full Details
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
